/**
 * 426 (2), the arrangement in the closed wood — the game trails (DECISIONS 426, 434; ARCHITECTURE §5.50). A real wood is
 * threaded by the paths its animals wear: narrow, winding with the ground, from the deep wood out to its edge. Here a
 * trail is a walk on the ground from a closed cell's centre in steps of `trailStep` people: each step the cheapest on
 * foot — the route graph's own grade rule (constants/action.ts ROUTE: a climb dear, a refused grade impassable) — of the
 * headings within `trailBend` of the trail's own, a bend costing a little, with a seeded drift a step so it winds on flat
 * ground; it ends after `trailPeople` or where the stands' read leaves the closed regime (the trail comes out of the wood
 * at its edge). The trees of the stands it passes give way within half `trailWidth` of it (core/forest.ts standTrees).
 * (The route graph's edges were the first thought: its vertices are 46 people apart on the megaplanet — an edge is a
 * cell — so a walk on them is a straight chord, not a trail; the ground walk takes the graph's cost rule instead.) Pure
 * and deterministic: the planet's seed draws which cells carry a trail (`trailChance`), its heading and its drift; the
 * trig is dmath's. No one walks it yet — a bot's route does not know it (a trail as cheap transit for the foot way, and
 * the ground's wear drawn on it, are later steps).
 */
import type { PlanetState, Vec3 } from '../types';
import { SeededRandom } from './random';
import { dcos, dsin } from './dmath';
import { cellCentre } from './cells';
import { tangentBasis } from './biomes';
import { getSurfaceRadius } from './surface';
import { ROUTE } from '../constants/action';
import { WOODS_STANDS, standFullness, woodsStandAt } from './woodsDensity';

const norm = (v: Vec3): Vec3 => { const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };
const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
/** The candidate bends a step may take (radians), the straight one first. */
const BENDS = [0, 1 / 3, -1 / 3, 2 / 3, -2 / 3, 1, -1];

/** A trail: its points (a step apart, the first at its cell's centre), and a bound — a centre and the angle from it that holds every point (the cut's first test). */
export interface WoodsTrail { points: Vec3[]; centre: Vec3; reach: number }

/**
 * The trail that starts in `cell`, or null: the cell drew none (`trailChance`), its centre is not in the closed regime, or
 * no step was open. Memoised by the caller.
 */
export function woodsTrailOf(planet: PlanetState, planetSeed: string, windSeed: string, person: number, n: number, cell: number): WoodsTrail | null {
  const points = trailPoints(planet, planetSeed, windSeed, person, n, cell);
  if (!points) return null;
  let cx = 0, cy = 0, cz = 0;
  for (const q of points) { cx += q.x; cy += q.y; cz += q.z; }
  const centre = norm({ x: cx, y: cy, z: cz });
  let least = 1;
  for (const q of points) least = Math.min(least, dot(q, centre));
  return { points, centre, reach: Math.sqrt(Math.max(0, 2 - 2 * least)) };   // (the chord to the farthest point: an angle's chord, a little over the angle)
}

function trailPoints(planet: PlanetState, planetSeed: string, windSeed: string, person: number, n: number, cell: number): Vec3[] | null {
  const rng = new SeededRandom(`${planetSeed}|trail|${cell}`);
  if (rng.next() >= WOODS_STANDS.trailChance) return null;
  let d = norm(cellCentre(cell, n));
  if (standFullness(woodsStandAt(planet, windSeed, person, d)) < 1) return null;
  const P = person / planet.radius, step = WOODS_STANDS.trailStep * P, stepWorld = WOODS_STANDS.trailStep * person;
  const [t1, t2] = tangentBasis(d), th = rng.range(0, 2 * Math.PI), cp = dcos(th), sp = dsin(th);
  let heading: Vec3 = norm({ x: t1.x * cp + t2.x * sp, y: t1.y * cp + t2.y * sp, z: t1.z * cp + t2.z * sp });
  const out: Vec3[] = [d];
  let r = getSurfaceRadius(planet, d);
  const steps = Math.floor(WOODS_STANDS.trailPeople / WOODS_STANDS.trailStep);
  for (let i = 0; i < steps; i++) {
    // the seeded drift, then the cheapest of the bends on foot
    const w = rng.range(-WOODS_STANDS.trailWander, WOODS_STANDS.trailWander), side = cross(d, heading), cw = dcos(w), sw = dsin(w);
    heading = norm({ x: heading.x * cw + side.x * sw, y: heading.y * cw + side.y * sw, z: heading.z * cw + side.z * sw });
    const side2 = cross(d, heading);
    let best: Vec3 | null = null, bestR = 0, bestCost = Infinity;
    for (const b of BENDS) {
      const a = b * WOODS_STANDS.trailBend, ca = dcos(a), sa = dsin(a);
      const dir = { x: heading.x * ca + side2.x * sa, y: heading.y * ca + side2.y * sa, z: heading.z * ca + side2.z * sa };
      const next = norm({ x: d.x + dir.x * step, y: d.y + dir.y * step, z: d.z + dir.z * step });
      const rn = getSurfaceRadius(planet, next), grade = (rn - r) / stepWorld;
      if (grade > ROUTE.footMaxGrade) continue;
      const cost = (1 + ROUTE.footUphill * Math.max(0, grade) + ROUTE.footDownhill * Math.max(0, -grade - ROUTE.footEasyDown)) * (1 + WOODS_STANDS.trailBendCost * Math.abs(a));
      if (cost < bestCost) { bestCost = cost; best = next; bestR = rn; }
    }
    if (!best) break;
    const moved = norm({ x: best.x - d.x, y: best.y - d.y, z: best.z - d.z }), up = dot(moved, best);
    heading = norm({ x: moved.x - best.x * up, y: moved.y - best.y * up, z: moved.z - best.z * up });
    d = best; r = bestR;
    out.push(d);
    if (standFullness(woodsStandAt(planet, windSeed, person, d)) < 1) break;   // (out of the wood at its edge: the trail ends there)
  }
  return out.length > 1 ? out : null;
}

/**
 * Whether `d` lies within `halfWidth` (radians) of a trail's line: the nearest point of any of its segments, in the tangent
 * plane at `d` (a segment is a step long — a small angle — so the plane holds). A trail whose bound does not hold `d` is
 * skipped on one dot product (most of them: a cell's neighbourhood holds a dozen or more).
 */
export function onWoodsTrail(d: Vec3, trails: ReadonlyArray<WoodsTrail>, halfWidth: number): boolean {
  const h2 = halfWidth * halfWidth;
  let t1: Vec3 | null = null, t2: Vec3 | null = null;
  for (const w of trails) {
    const chord = w.reach + halfWidth * 1.01;   // (chords: the bound's and the width's, the width's a little over)
    if (2 - 2 * dot(d, w.centre) > chord * chord) continue;
    if (!t1 || !t2) [t1, t2] = tangentBasis(d);
    const tr = w.points;
    for (let i = 1; i < tr.length; i++) {
      const a = tr[i - 1], b = tr[i];
      // (a segment whose ends are both far from d cannot pass near it: a step is far shorter than the cull)
      if (dot(a, d) < 0.999 && dot(b, d) < 0.999) continue;
      const ax = dot(a, t1), ay = dot(a, t2), bx = dot(b, t1), by = dot(b, t2), ex = bx - ax, ey = by - ay;
      const l2 = ex * ex + ey * ey, t = l2 > 0 ? Math.max(0, Math.min(1, -(ax * ex + ay * ey) / l2)) : 0;
      const px = ax + ex * t, py = ay + ey * t;
      if (px * px + py * py < h2) return true;
    }
  }
  return false;
}
